import React, { useEffect, useRef, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import NavBar from './Navbar';
import DisclaimerModal from './DisclaimerModal';
import OnboardingWizard from './OnboardingWizard';
import { useSelector, useDispatch } from 'react-redux';
import { Alert, Snackbar } from '@mui/material';
import { fetchDisclaimerStatus } from '../features/disclaimer/disclaimerSlice';
import { clearNotification } from '../features/notifications/notificationsSlice';
import { fetchUserSettings } from '../features/userSettings/userSettingsSlice';

const Layout = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const user = useSelector((state) => state.account.user);
  const logoutReason = useSelector((state) => state.account.logoutReason);
  const disclaimerStatus = useSelector((state) => state.disclaimer.status);
  const globalNotification = useSelector((state) => state.notifications?.notification);
  const userSettings = useSelector((state) => state.userSettings.settings);
  const userSettingsLoaded = useSelector((state) => state.userSettings.loaded);
  const userSettingsError = useSelector((state) => state.userSettings.error);
  const [logoutNotice, setLogoutNotice] = useState({ open: false, message: '' });
  const [showOnboarding, setShowOnboarding] = useState(false);
  const previousUserRef = useRef(user);
  const previousReasonRef = useRef(logoutReason);

  // Fetch user settings when user logs in
  useEffect(() => {
    if (user && !userSettingsLoaded) {
      dispatch(fetchUserSettings());
    }
  }, [user, userSettingsLoaded, dispatch]);

  // Check if onboarding should be shown.
  //
  // Field-name compatibility note: the backend writes `onboardingCompleted`
  // (with a 'd') in userSettingsHandler when the wizard is finished, but
  // older code paths and some legacy accounts use `onboardingComplete`
  // (no 'd'). Read both — without this, the wizard would silently re-show
  // on every navigation even after the user successfully completed it,
  // because the frontend was looking at a field that the backend never
  // actually writes.
  const onboardingDone =
    userSettings.onboardingCompleted === true || userSettings.onboardingComplete === true;

  useEffect(() => {
    // Only show wizard if:
    // 1. User is authenticated
    // 2. Settings loaded successfully (no error state from failed fetch)
    // 3. Onboarding has not been completed (under either field name)
    const shouldShowWizard = user &&
      userSettingsLoaded &&
      !userSettingsError &&
      !onboardingDone;

    if (shouldShowWizard) {
      // Only show onboarding if not on login/register/verify pages
      const excludedPaths = ['/login', '/register', '/verify', '/'];
      if (!excludedPaths.includes(location.pathname)) {
        setShowOnboarding(true);
      }
    } else {
      // Reset wizard state when user logs out, onboarding is complete, or fetch failed
      setShowOnboarding(false);
    }
  }, [user, userSettingsLoaded, userSettingsError, onboardingDone, location.pathname]);

  // Fetch disclaimer status when user logs in
  useEffect(() => {
    if (user && !disclaimerStatus.loaded && !disclaimerStatus.loading) {
      dispatch(fetchDisclaimerStatus());
    }
  }, [user, disclaimerStatus.loaded, disclaimerStatus.loading, dispatch]);

  // Note: Permission fetching is handled by usePermissions hook

  useEffect(() => {
    const previousUser = previousUserRef.current;
    const previousReason = previousReasonRef.current;

    if (previousUser && !user) {
      const effectiveReason = logoutReason || previousReason || 'manual';
      const message = effectiveReason === 'expired'
        ? 'Your session expired. Please sign in again.'
        : 'You have been logged out.';

      setLogoutNotice({ open: true, message });

      if (location.pathname !== '/login') {
        navigate('/login', { replace: true });
      }
    }

    previousUserRef.current = user;
    previousReasonRef.current = logoutReason;
  }, [user, logoutReason, location.pathname, navigate]);

  const handleLogoutNoticeClose = (_, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setLogoutNotice((prev) => ({ ...prev, open: false }));
  };

  const handleGlobalNotificationClose = (_, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    dispatch(clearNotification());
  };

  const handleOnboardingComplete = () => {
    setShowOnboarding(false);
    // Navigate to markets page
    navigate('/today');
  };

  return (
    <div>
      <NavBar />
      <DisclaimerModal />
      <OnboardingWizard open={showOnboarding} onComplete={handleOnboardingComplete} />
      <main>
        <Outlet /> {/* This will render the child route components */}
      </main>
      <Snackbar
        open={logoutNotice.open}
        autoHideDuration={6000}
        onClose={handleLogoutNoticeClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={handleLogoutNoticeClose}
          severity="info"
          sx={{ width: '100%' }}
        >
          {logoutNotice.message}
        </Alert>
      </Snackbar>
      {/* Global notification for permission errors and system alerts */}
      <Snackbar
        key={globalNotification?.timestamp}
        open={!!globalNotification}
        autoHideDuration={globalNotification?.autoHide !== false ? 6000 : null}
        onClose={handleGlobalNotificationClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleGlobalNotificationClose}
          severity={globalNotification?.severity || 'info'}
          sx={{ width: '100%' }}
        >
          {globalNotification?.message}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default Layout;
